/**
 * Single source of truth for React Query keys.
 *
 * Why: the codebase used to mix singular (`['expense', ...]`) and plural
 * (`['expenses', ...]`) for the same entity. Invalidations only covered one
 * form, so mutations left the other half of the cache stale and the UI
 * stopped refreshing on its own. This file pins the canonical shape of every
 * key so call sites can never drift again.
 *
 * Convention:
 *   - Singular entity name as first segment (matches `entityName.toLowerCase()`
 *     used by the factories in `useCompanyData.jsx`).
 *   - `companyId` as second segment so React Query's prefix-based invalidation
 *     can wipe a single tenant when needed.
 *   - Subsegments (`'paginated'`, `'chart'`, `'stats-month'`) live BETWEEN
 *     entity and companyId so a bare `['expense']` invalidates everything.
 *   - Para las entidades de CRM y operativo, el primer segmento va en
 *     plural-kebab (`'deal-lost-reasons'`, `'contact-directory'`) — es lo que ya
 *     usaba la mayoría de lecturas cuando se fijó la convención.
 *
 * DÓNDE VIVEN AHORA. Desde Fase 0 / PR 0.5c las definiciones están repartidas en
 * `src/features/<modulo>/qk.ts` y este fichero solo las compone. Para añadir una
 * clave, edita el fichero de su dominio: aquí no hay que tocar nada.
 *
 * Este fichero sigue siendo el ÚNICO punto de import (`@/lib/qk`) — los 162
 * ficheros que ya lo importaban no se han tocado.
 *
 * `qkShape.test.ts` congela la forma de las 186 hojas: una clave que cambie de
 * forma es una entrada de caché distinta, y el síntoma no es un error sino una
 * pantalla que deja de refrescarse sola.
 */
import { platformKeys } from '@/features/platform/qk';
import { billingKeys } from '@/features/billing/qk';
import { operationsKeys } from '@/features/operations/qk';
import { accountingKeys } from '@/features/accounting/qk';
import { bankingKeys } from '@/features/banking/qk';
import { hrKeys } from '@/features/hr/qk';
import { fiscalKeys } from '@/features/fiscal/qk';
import { aiKeys } from '@/features/ai/qk';

export const qk = {
  ...platformKeys,
  ...billingKeys,
  ...operationsKeys,
  ...accountingKeys,
  ...bankingKeys,
  ...hrKeys,
  ...fiscalKeys,
  ...aiKeys,
} as const;
